import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Camera, Eye, Folder, Home, ChevronRight } from "lucide-react";
import api from "../utils/api";
import MediaViewer from "../components/MediaViewer";

// Get folder ID from URL parameters
const getFolderIdFromUrl = (search) => {
  const urlParams = new URLSearchParams(search);
  const folderId = urlParams.get("folder");
  return folderId ? parseInt(folderId) : null;
};

const Gallery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [currentFolderId, setCurrentFolderId] = useState(() =>
    getFolderIdFromUrl(location.search)
  );
  const [loading, setLoading] = useState(true);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = [
    { value: "all", label: "All" },
    { value: "perunnal", label: "Perunnal" },
    { value: "consecration", label: "Consecration" },
    { value: "construction", label: "Construction" },
    { value: "holyweek", label: "Holy Week" },
    { value: "others", label: "Others" },
  ];

  // Listen for URL changes (browser back/forward navigation)
  useEffect(() => {
    const newFolderId = getFolderIdFromUrl(location.search);

    if (newFolderId !== currentFolderId) {
      setCurrentFolderId(newFolderId);
      // Update currentFolder state based on folderId
      if (newFolderId && folders.length > 0) {
        const folder = folders.find((f) => f._id === newFolderId);
        setCurrentFolder(folder || null);
      } else {
        setCurrentFolder(null);
      }
    }
  }, [location.search, currentFolderId, folders]);

  // Initialize currentFolder from URL after folders are loaded
  useEffect(() => {
    if (currentFolderId && folders.length > 0 && !currentFolder) {
      const folder = folders.find((f) => f._id === currentFolderId);
      setCurrentFolder(folder || null);
    }
  }, [currentFolderId, folders, currentFolder]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, currentFolder]);

  const fetchData = async () => {
    await Promise.all([fetchGalleryImages(), fetchFolders()]);
  };

  const fetchGalleryImages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (activeCategory && activeCategory !== "all") {
        params.append("category", activeCategory);
      }

      // Add folder filtering logic
      if (currentFolder === null) {
        // Fetch images not in any folder (main gallery view)
        params.append("folderId", "null");
      } else if (currentFolder) {
        // Fetch images in specific folder
        params.append("folderId", currentFolder._id.toString());
      }

      const response = await api.get(`/api/gallery?${params.toString()}`);
      setGalleryImages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching gallery images:", error);
      setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      setFoldersLoading(true);
      const params = new URLSearchParams();
      if (activeCategory && activeCategory !== "all") {
        params.append("category", activeCategory);
      }

      const response = await api.get(
        `/api/gallery/folders?${params.toString()}`
      );
      setFolders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching folders:", error);
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  };

  // Folder navigation functions
  const handleEnterFolder = (folder) => {
    setCurrentFolder(folder);
    setCurrentFolderId(folder._id);

    // Update URL with folder parameter
    const currentParams = new URLSearchParams(location.search);
    currentParams.set("folder", folder._id.toString());
    navigate(`${location.pathname}?${currentParams.toString()}`, {
      replace: false,
    });
  };

  const handleExitFolder = () => {
    setCurrentFolder(null);
    setCurrentFolderId(null);

    // Remove folder parameter from URL
    const currentParams = new URLSearchParams(location.search);
    currentParams.delete("folder");
    navigate(`${location.pathname}?${currentParams.toString()}`, {
      replace: false,
    });
  };

  const openModal = (image) => {
    const imageIndex = galleryImages.findIndex((img) => img._id === image._id);
    setCurrentImageIndex(imageIndex);
    setSelectedImage(image);
  };

  const closeModal = () => {
    setSelectedImage(null);
    setCurrentImageIndex(0);
  };

  const goToPrevious = useCallback(() => {
    if (galleryImages.length > 1) {
      const newIndex =
        currentImageIndex === 0
          ? galleryImages.length - 1
          : currentImageIndex - 1;
      setCurrentImageIndex(newIndex);
      setSelectedImage(galleryImages[newIndex]);
    }
  }, [currentImageIndex, galleryImages]);

  const goToNext = useCallback(() => {
    if (galleryImages.length > 1) {
      const newIndex =
        currentImageIndex === galleryImages.length - 1
          ? 0
          : currentImageIndex + 1;
      setCurrentImageIndex(newIndex);
      setSelectedImage(galleryImages[newIndex]);
    }
  }, [currentImageIndex, galleryImages]);

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-r from-red-800 to-red-600 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Photo Gallery</h1>
          <p className="text-xl max-w-3xl mx-auto leading-relaxed">
            Capturing precious moments from our church community and
            celebrations.
          </p>
        </div>
      </section>

      <section className="py-20 bg-amber-50">
        <div className="max-w-6xl mx-auto px-4">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center mb-12 gap-4">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setActiveCategory(category.value)}
                className={`px-6 py-2 rounded-full font-medium transition-colors ${
                  activeCategory === category.value
                    ? "bg-red-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Breadcrumb Navigation */}
          {currentFolder && (
            <div className="flex items-center text-sm text-gray-600 mb-[1.5rem]">
              <button
                onClick={handleExitFolder}
                className="flex items-center hover:text-red-600 transition-colors"
              >
                <Home className="w-4 h-4 mr-1" />
                Gallery
              </button>
              <ChevronRight className="w-4 h-4 mx-1" />
              <span className="font-medium text-red-600">
                {currentFolder.name}
              </span>
            </div>
          )}

          {/* Single Loading State for Everything */}
          {loading || foldersLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading gallery content...</p>
            </div>
          ) : (
            <>
              {/* Folders Section - only show when not inside a folder */}
              {!currentFolder && folders.length > 0 && (
                <div className="mb-12">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Folders
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6 mb-12">
                    {folders.map((folder) => (
                      <div
                        key={folder._id}
                        className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow cursor-pointer border"
                        onClick={() => handleEnterFolder(folder)}
                      >
                        <div className="p-6">
                          <div className="flex flex-col items-center space-y-3">
                            <Folder className="w-12 h-12 text-red-600" />
                            <span className="text-sm font-semibold text-gray-900 text-center break-words">
                              {folder.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {folder.imageCount} image
                              {folder.imageCount !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Images Section */}
              {galleryImages.length > 0 ? (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {galleryImages.map((image) => (
                      <div
                        key={image._id}
                        className="group relative overflow-hidden rounded-lg shadow-lg cursor-pointer"
                        onClick={() => openModal(image)}
                      >
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                          <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
                          <h3 className="text-white font-semibold">
                            {image.title}
                          </h3>
                          <p className="text-gray-300 text-sm capitalize">
                            {image.category}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : galleryImages.length === 0 && folders.length === 0 ? (
                <div className="text-center py-12">
                  <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No Images Found
                  </h3>
                  <p className="text-gray-500">
                    No images in this category yet.
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>

      {/* MediaViewer Modal */}
      <MediaViewer
        isOpen={!!selectedImage}
        onClose={closeModal}
        resource={
          selectedImage
            ? {
                resource_url: selectedImage.src,
                resource_name: selectedImage.title,
                file_name: selectedImage.src.split("/").pop(),
              }
            : null
        }
        title={selectedImage?.title || ""}
        showNavigation={galleryImages.length > 1}
        onPrevious={galleryImages.length > 1 ? goToPrevious : undefined}
        onNext={galleryImages.length > 1 ? goToNext : undefined}
        currentIndex={currentImageIndex}
        totalCount={galleryImages.length}
      />
    </div>
  );
};

export default Gallery;
